// src/pages/RegisterVerifyOtp.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { KeyRound, ShieldCheck, Loader, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const RegisterVerifyOtp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const state = (location.state || {}) as {
    userType?: 'doctor' | 'public';
    email?: string;
    devOtp?: string;
  };

  const userType = state.userType === 'public' ? 'public' : 'doctor';
  const email = state.email;
  const isPublic = userType === 'public';
  const endpointBase = isPublic ? '/public/register' : '/doctor/register';

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [devOtp, setDevOtp] = useState<string | undefined>(state.devOtp);
  const otpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!email) {
      navigate('/register', { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  if (!email) return null;

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');

    const trimmed = otp.trim();
    if (trimmed.length !== 6) {
      setError('Enter the 6-digit verification code');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await api.post(`${endpointBase}/verify-otp`, {
        email,
        otp: trimmed,
      });

      const registrationToken = response.data?.data?.registrationToken;
      if (!response.data?.success || !registrationToken) {
        const message = response.data?.message || 'Verification failed. Please try again.';
        setError(message);
        toast.error(message);
        return;
      }

      toast.success('Email verified');
      if (isPublic) {
        navigate('/register/select-plan', {
          state: { userType: 'public', email, registrationToken },
        });
      } else {
        navigate('/register/professional-details', {
          state: { userType: 'doctor', email, registrationToken },
        });
      }
    } catch (err: any) {
      const message =
        err.response?.data?.message || 'Invalid or expired verification code.';
      setError(message);
      toast.error(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      const response = await api.post(`${endpointBase}/resend-otp`, { email });
      const newOtp = response.data?.data?.otp;
      if (newOtp) setDevOtp(newOtp);
      setResendCooldown(60);
      setOtp('');
      otpInputRef.current?.focus();
      toast.success('A new verification code has been sent');
    } catch (err: any) {
      const message =
        err.response?.data?.message || 'Failed to resend verification code.';
      setError(message);
      toast.error(message);
    }
  };

  return (
    <div className="relative w-screen min-h-screen md:h-screen flex flex-col md:flex-row overflow-x-hidden md:overflow-hidden bg-white">
      {/* Asymmetric Curved Divider SVG (desktop) */}
      <div className="absolute inset-0 w-full h-full pointer-events-none z-[1] hidden md:block" aria-hidden="true">
        <svg className="w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="none">
          <defs>
            <linearGradient id="regOtpTealGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#26ABB0" />
              <stop offset="100%" stopColor="#219FA4" />
            </linearGradient>
          </defs>
          <path
            d="M 526.3 0 C 464.5 333.3, 475.4 666.7, 602.3 1000 L 1000 1000 L 1000 0 Z"
            fill="url(#regOtpTealGrad)"
          />
        </svg>
      </div>

      {/* LEFT PANEL: Branding */}
      <div className="relative w-full md:w-1/2 h-auto md:h-full flex flex-col justify-between px-6 py-4 sm:p-11 lg:p-14 z-[2] bg-white md:bg-transparent shrink-0">
        <div className="my-3 sm:my-4 md:my-0 md:absolute md:top-[43%] md:left-[50%] md:-translate-x-1/2 md:-translate-y-1/2 flex flex-col items-center justify-center text-center w-full max-w-[90%] md:max-w-[720px] lg:max-w-[820px] xl:max-w-[880px] px-2">
          <img
            src="/logo/DiagnomIQ-01.svg"
            alt="DiagnomIQ - Precision insights for every patient"
            className="w-full max-w-[320px] sm:max-w-[460px] md:max-w-[640px] lg:max-w-[740px] xl:max-w-[800px] h-auto object-contain"
          />
        </div>
      </div>

      {/* RIGHT PANEL: OTP form on Teal Background */}
      <div className="relative w-full md:w-1/2 flex-1 md:h-full flex items-center justify-center p-6 sm:p-10 lg:p-14 z-[2] bg-[#23A4A8] md:bg-transparent rounded-t-[32px] md:rounded-none shadow-lg md:shadow-none min-h-[520px]">
        {/* Top-Right Decorative Arcs */}
        <svg
          className="hidden md:block absolute top-0 right-0 w-[320px] lg:w-[450px] h-[320px] lg:h-[450px] pointer-events-none z-[1]"
          viewBox="0 0 450 450"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="492" cy="-25" r="225" fill="#2FAFB1" />
          <circle cx="492" cy="-25" r="162" fill="#48B9BC" />
        </svg>

        {/* Bottom-Right Decorative Arcs */}
        <svg
          className="hidden md:block absolute bottom-0 right-0 w-[380px] lg:w-[550px] h-[380px] lg:h-[550px] pointer-events-none z-[1]"
          viewBox="0 0 550 550"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="578" cy="651" r="298" fill="#33AAAD" />
          <circle cx="578" cy="651" r="244" fill="#44B6B9" />
        </svg>

        <div className="relative z-[3] w-full max-w-[465px] space-y-5">
          <div className="space-y-2">
            <div className="inline-flex p-2.5 rounded-full bg-white/20 text-white mb-1.5">
              <ShieldCheck className="h-6 w-6 sm:h-7 sm:w-7" />
            </div>
            <h1 className="text-[26px] sm:text-[30px] font-bold text-white tracking-tight leading-tight">
              Verify Your Email
            </h1>
            <p className="text-[14.5px] sm:text-[15.5px] text-[#E0F7F6] leading-relaxed">
              A 6-digit verification code was sent to{' '}
              <span className="font-semibold text-white">{email}</span>. Enter it below to
              verify your account.
            </p>
          </div>

          <form onSubmit={handleVerify} className="space-y-4 sm:space-y-5">
            <div>
              <label htmlFor="otp" className="block text-[14.5px] sm:text-[15px] font-semibold text-white mb-2">
                Verification Code
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <KeyRound className="h-5 w-5 text-[#94A3B8]" />
                </div>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onPaste={(e) => {
                    e.preventDefault();
                    const digits = e.clipboardData
                      .getData('text')
                      .replace(/\D/g, '')
                      .slice(0, 6);
                    if (digits) setOtp(digits);
                  }}
                  ref={otpInputRef}
                  className="w-full h-[54px] sm:h-[56px] bg-white rounded-md pl-12 pr-4 text-center text-lg font-mono tracking-[0.3em] text-[#1E293B] placeholder-[#94A3B8] outline-none shadow-sm focus:ring-2 focus:ring-[#097173]"
                  placeholder="••••••"
                />
              </div>
              {error && <p className="mt-1.5 text-xs sm:text-sm text-red-100 font-medium">{error}</p>}
            </div>

            <div>
              <button
                type="submit"
                disabled={isVerifying}
                className="w-full h-[54px] sm:h-[56px] bg-[#097173] hover:bg-[#075E60] active:scale-[0.99] text-white font-semibold text-[16px] rounded-md flex items-center justify-center space-x-2 shadow-lg transition-all duration-150 disabled:opacity-60 cursor-pointer"
              >
                {isVerifying ? (
                  <>
                    <Loader className="h-5 w-5 mr-2 animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <span>Verify</span>
                    <ArrowRight className="h-5 w-5 ml-1" />
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || isVerifying}
                className="text-[14px] font-medium text-white hover:text-[#D8F2F1] underline disabled:opacity-50 disabled:no-underline cursor-pointer transition-colors"
              >
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
              </button>

              <Link
                to="/register"
                className="text-[14px] font-medium text-[#E0F7F6] hover:text-white transition-colors"
              >
                Change details
              </Link>
            </div>
          </form>

          {process.env.NODE_ENV !== 'production' && devOtp && (
            <div className="fixed bottom-4 right-4 z-[100] bg-yellow-300 text-black text-xs font-mono px-3 py-2 rounded shadow-lg">
              Dev OTP: <span className="font-bold">{devOtp}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterVerifyOtp;
